import React from 'react';

import type { SignatureFieldEntry } from '@aline/common';
import { getIsInitialsSignatureField, Select } from '@aline/common';
import { Typography } from '@mui/material';
import type { ExistingFieldRecipient } from 'src/components';
import { AlineAvatar, AlineButton, AlineTooltip } from 'src/components';
import { useOnClickNextField } from 'src/editor/popovers/fields/helpers';
import { ClientTransforms } from 'src/editor/transforms';
import { useSelectCurrentUser } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails } from 'src/helpers';
import { getSignButtonText } from 'src/helpers/signature-labels';
import { useAuthorizedTransforms } from 'src/hooks';
import { signatureDialogOpenVar, useEditor } from 'src/layout-engine';
import type { UserType } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  assigneeText: {
    fontWeight: 600,
  },
  avatarTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
    overflow: 'hidden',
  },
  contentWrapper: { padding: theme.spacing(2) },
  emailText: {
    fontSize: 12,
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.appColors.fieldHeader,
    borderRadius: '4px 4px 0px 0px',
    display: 'flex',
    flexWrap: 'nowrap',
    padding: theme.spacing(1, 2),
  },
}));

type SignHereFieldAction = ExistingFieldRecipient & { completedAt: string | null };
interface SignHereField extends Pick<Apollo.SignatureField, 'id' | 'nodeId' | '__typename'> {
  action: SignHereFieldAction | null;
}

interface SignHerePopoverContentProps {
  declined: boolean;
  disabledReason: string;
  inputField: SignHereField;
  signatureFieldEntry: SignatureFieldEntry;
}

export function SignHerePopoverContent({
  declined,
  disabledReason,
  inputField,
  signatureFieldEntry,
}: SignHerePopoverContentProps): JSX.Element | null {
  const [signatureField, path] = signatureFieldEntry;
  const { classes } = useStyles();
  const editor = useEditor();
  const { onClickNext } = useOnClickNextField();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { currentUser, signatureImg, signatureInitialsImg } = useGetUserSignatureImg();
  const isInitials = getIsInitialsSignatureField(signatureField);
  const stampImg = isInitials ? signatureInitialsImg : signatureImg;
  const isSigned = Boolean(signatureField.signatureImgId);
  const buttonText = getSignButtonText({ isInitials, isSigned });

  async function signOrClearSignature(e: React.MouseEvent): Promise<void> {
    if (!currentUser || !inputField?.action?.assignee?.id) return;

    const isCompleted = Boolean(inputField.action?.completedAt);
    if (isCompleted) return;

    if (!stampImg) {
      signatureDialogOpenVar(true);
      return;
    }

    const signatureImgId = !isSigned ? stampImg.id : null;

    AuthorizedTransforms.signOrClearSignatureField(editor, {
      assigneeId: inputField.action.assignee.id,
      sigFieldPath: path,
      signatureImgId,
    });
    // If the user is signing, immediately navigate to the next field on clicking sign here.
    if (signatureImgId) await onClickNext(e, inputField);
    ClientTransforms.focus(editor);
  }

  return (
    <>
      <SignHereHeader inputField={inputField} />

      <div className={classes.contentWrapper}>
        {declined ? (
          <Typography color="textSecondary">Declined</Typography>
        ) : (
          <AlineTooltip arrow placement="top" title={disabledReason}>
            <div>
              <AlineButton
                fullWidth
                color="primary"
                data-cy={Select.document.editor.popper.signHere.signButton}
                disabled={Boolean(disabledReason)}
                variant={isSigned ? 'outlined' : 'contained'}
                onClick={signOrClearSignature}
              >
                {buttonText}
              </AlineButton>
            </div>
          </AlineTooltip>
        )}
      </div>
    </>
  );
}

interface SignHereHeaderProps {
  inputField: SignHereField;
}

function SignHereHeader({ inputField }: SignHereHeaderProps): JSX.Element {
  const { classes } = useStyles();
  const { avatarColor, primary, secondary } = getAssigneeDisplayDetails(
    inputField.action?.assignee
  );

  return (
    <div className={classes.header}>
      <AlineTooltip arrow placement="bottom" title={primary}>
        <AlineAvatar
          avatarInfo={inputField.action?.assignee}
          styles={{ backgroundColor: avatarColor, size: 'md' }}
        />
      </AlineTooltip>
      <div className={classes.avatarTextWrapper}>
        <Typography noWrap className={classes.assigneeText}>
          {primary}
        </Typography>
        {!!secondary && (
          <Typography noWrap className={classes.emailText} color="textSecondary">
            {secondary}
          </Typography>
        )}
      </div>
    </div>
  );
}

/**
 * Hook used to get a user's signature image info.
 */
export function useGetUserSignatureImg(): {
  currentUser: UserType;
  signatureImg: NonNullable<UserType>['settings']['signatureImg'];
  signatureInitialsImg: NonNullable<UserType>['settings']['signatureInitialsImg'];
} {
  const currentUser = useSelectCurrentUser();

  return {
    currentUser,
    signatureImg: currentUser?.settings.signatureImg ?? null,
    signatureInitialsImg: currentUser?.settings.signatureInitialsImg ?? null,
  };
}
